import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';
import EmployeeScheduleDialog from './EmployeeScheduleDialog';
import SalaryConfigDialog from './SalaryConfigDialog';
import WorklogDialog from './WorklogDialog';
import WorkSessionDialog from './WorkSessionDialog';

function EmployeesPanel({ user, token }) {
    const [employees, setEmployees] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [scheduleEmployee, setScheduleEmployee] = useState(null);
    const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
    const [worklogEmployee, setWorklogEmployee] = useState(null);
    const [openWorklogDialog, setOpenWorklogDialog] = useState(false);
    const [workSessionEmployee, setWorkSessionEmployee] = useState(null);
    const [openWorkSessionDialog, setOpenWorkSessionDialog] = useState(false);
    const [salaryConfigEmployee, setSalaryConfigEmployee] = useState(null);
    const [openSalaryConfigDialog, setOpenSalaryConfigDialog] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        position: '',
        phone: '',
        email: '',
        hourlyRate: '',
        status: 'active',
        notes: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        try {
            const response = await fetch(`${API_URL}/api/employees`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setEmployees(data.employees);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const handleOpenDialog = (employee = null) => {
        if (employee) {
            setEditingEmployee(employee);
            setFormData({
                fullName: employee.full_name || '',
                position: employee.position || '',
                phone: employee.phone || '',
                email: employee.email || '',
                hourlyRate: employee.hourly_rate || '',
                status: employee.status || 'active',
                notes: employee.notes || ''
            });
        } else {
            setEditingEmployee(null);
            setFormData({
                fullName: '',
                position: '',
                phone: '',
                email: '',
                hourlyRate: '',
                status: 'active',
                notes: ''
            });
        }
        setOpenDialog(true);
        setError('');
        setSuccess('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingEmployee(null);
        setError('');
    };

    const handleSubmit = async () => {
        try {
            const url = editingEmployee
                ? `${API_URL}/api/employees/${editingEmployee.id}`
                : `${API_URL}/api/employees`;

            const response = await fetch(url, {
                method: editingEmployee ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!data.success) {
                setError(data.error);
                return;
            }

            setSuccess(editingEmployee ? 'Сотрудник обновлён' : 'Сотрудник добавлен');
            handleCloseDialog();
            fetchEmployees();
        } catch (error) {
            setError('Ошибка при сохранении');
            console.error('Error saving employee:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить этого сотрудника?')) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/employees/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (!data.success) {
                alert(data.error);
                return;
            }

            setSuccess('Сотрудник удалён');
            fetchEmployees();
        } catch (error) {
            alert('Ошибка при удалении');
            console.error('Error deleting employee:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'inactive':
                return 'warning';
            case 'fired':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active':
                return 'Активен';
            case 'inactive':
                return 'Неактивен';
            case 'fired':
                return 'Уволен';
            default:
                return status;
        }
    };

    const isOwner = user?.role === 'owner';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="large" />
                    Сотрудники
                </Typography>
                {isOwner && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Добавить сотрудника
                    </Button>
                )}
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ФИО</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Должность</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Телефон</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ставка за смену</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Статус</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Аккаунт</TableCell>
                            {isOwner && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Действия</TableCell>}
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>График</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {employees.map((employee) => (
                            <TableRow key={employee.id} hover>
                                <TableCell>{employee.full_name}</TableCell>
                                <TableCell>{employee.position || '-'}</TableCell>
                                <TableCell>{employee.phone || '-'}</TableCell>
                                <TableCell>{employee.email || '-'}</TableCell>
                                <TableCell>{employee.hourly_rate ? `₽${employee.hourly_rate}/ч` : '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={getStatusText(employee.status)}
                                        color={getStatusColor(employee.status)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {employee.username ? (
                                        <Chip label={employee.user_role} size="small" variant="outlined" />
                                    ) : '-'}
                                </TableCell>
                                {isOwner && (
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(employee)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(employee.id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Box display="flex" gap={0.5}>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                                setScheduleEmployee(employee);
                                                setOpenScheduleDialog(true);
                                            }}
                                            title="График работы"
                                        >
                                            <CalendarTodayIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() => {
                                                setWorklogEmployee(employee);
                                                setOpenWorklogDialog(true);
                                            }}
                                            title="Учет времени"
                                        >
                                            <AccessTimeIcon />
                                        </IconButton>
                                        <Tooltip title="Зарегистрировать смену">
                                            <IconButton
                                                size="small"
                                                color="info"
                                                onClick={() => {
                                                    setWorkSessionEmployee(employee);
                                                    setOpenWorkSessionDialog(true);
                                                }}
                                            >
                                                <WorkIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Настройки зарплаты">
                                            <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() => {
                                                    setSalaryConfigEmployee(employee);
                                                    setOpenSalaryConfigDialog(true);
                                                }}
                                            >
                                                <AttachMoneyIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Диалог добавления/редактирования */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingEmployee ? 'Редактировать сотрудника' : 'Добавить сотрудника'}
                </DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <TextField
                        fullWidth
                        label="ФИО *"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Должность"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        margin="normal"
                        select
                    >
                        <MenuItem value="Гид-экскурсовод">Гид-экскурсовод</MenuItem>
                        <MenuItem value="Администратор">Администратор</MenuItem>
                        <MenuItem value="Кассир">Кассир</MenuItem>
                        <MenuItem value="Охранник">Охранник</MenuItem>
                        <MenuItem value="Уборщик">Уборщик</MenuItem>
                        <MenuItem value="Владелец">Владелец</MenuItem>
                    </TextField>
                    <TextField
                        fullWidth
                        label="Телефон"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Ставка за смену (₽)"
                        type="number"
                        value={formData.hourlyRate}
                        onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Статус"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        margin="normal"
                        select
                    >
                        <MenuItem value="active">Активен</MenuItem>
                        <MenuItem value="inactive">Неактивен</MenuItem>
                        <MenuItem value="fired">Уволен</MenuItem>
                    </TextField>
                    <TextField
                        fullWidth
                        label="Заметки"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        margin="normal"
                        multiline
                        rows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingEmployee ? 'Сохранить' : 'Добавить'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог графика работы */}
            <EmployeeScheduleDialog
                open={openScheduleDialog}
                onClose={() => {
                    setOpenScheduleDialog(false);
                    setScheduleEmployee(null);
                }}
                employee={scheduleEmployee}
                token={token}
                user={user}
            />

            {/* Диалог учета рабочего времени */}
            <WorklogDialog
                open={openWorklogDialog}
                onClose={() => {
                    setOpenWorklogDialog(false);
                    setWorklogEmployee(null);
                }}
                employee={worklogEmployee}
                token={token}
            />

            {/* Диалог регистрации смены */}
            <WorkSessionDialog
                open={openWorkSessionDialog}
                onClose={() => {
                    setOpenWorkSessionDialog(false);
                    setWorkSessionEmployee(null);
                }}
                session={null}
                employees={employees}
                token={token}
                onSuccess={() => {
                    setSuccess('Смена зарегистрирована');
                    setTimeout(() => setSuccess(''), 3000);
                }}
            />

            {/* Диалог настроек зарплаты */}
            <SalaryConfigDialog
                open={openSalaryConfigDialog}
                onClose={() => {
                    setOpenSalaryConfigDialog(false);
                    setSalaryConfigEmployee(null);
                }}
                employee={salaryConfigEmployee}
                token={token}
                onSuccess={() => {
                    fetchEmployees();
                    setSuccess('Настройки зарплаты обновлены');
                    setTimeout(() => setSuccess(''), 3000);
                }}
            />
        </Box>
    );
}

export default EmployeesPanel;
